/**
 * TransportBar Component
 * Complete transport controls: Play, Record, Stop, Loop, Metronome, Count-In,
 * BPM, Time Signature, Quantize Controls, Undo/Redo, and Export.
 * Organized into four logical, ergonomic tool bays.
 */

import React from 'react';
import {
  Play,
  Square,
  Circle,
  Repeat,
  Sliders,
  Clock,
  Rewind,
  FastForward,
  SkipBack,
  Minus,
  Plus,
  ArrowRightToLine,
} from 'lucide-react';
import {
  DawViewMode,
  LoopRange,
  QuantizeGrid,
  QuantizeOptions,
  SplitLayoutConfig,
  TimeSignature,
  TransportPosition,
  TransportState,
} from '../types/midi';
import { formatPosition, formatTime } from '../core/timing';
import { AudioEngine } from '../core/audioEngine';
import { InstrumentSelector } from './InstrumentSelector';
import { PianoEffectsPopover } from './PianoEffectsPopover';

/** Metronome icon directly sourced from user SVG specifications */
const MetronomeIcon: React.FC<{ className?: string }> = ({ className = 'w-[11px] h-3.5' }) => (
  <svg
    viewBox="0 0 32 41"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M21.8887 0C23.3238 0.000277449 24.5582 1.0172 24.833 2.42578L25.3027 4.83398L23.1855 9.24414L22.2988 4H9.41992L5.35938 28H14.4424L15.9941 25H13.3594C12.8071 25 12.3594 24.5523 12.3594 24C12.3594 23.4477 12.8071 23 13.3594 23H17.0293L18.0635 21H13.3594C12.8071 21 12.3594 20.5523 12.3594 20C12.3594 19.4477 12.8071 19 13.3594 19H18.3594C18.5957 19 18.8123 19.0827 18.9834 19.2197L26.8877 3.93945C27.3951 2.95839 28.6019 2.57368 29.583 3.08105C30.564 3.58848 30.9477 4.79531 30.4404 5.77637L18.9453 28H26.3594L24.9199 19.4961L27.2256 14.6924L31.6621 37.4258C32.0233 39.278 30.6048 40.9997 28.7178 41H3.00195C1.11468 41 -0.304556 39.2781 0.0566406 37.4258L6.88672 2.42578C7.16162 1.01703 8.39573 0 9.83105 0H21.8887ZM18.3594 15C18.9117 15 19.3594 15.4477 19.3594 16C19.3594 16.5523 18.9117 17 18.3594 17H13.3594C12.8071 17 12.3594 16.5523 12.3594 16C12.3594 15.4477 12.8071 15 13.3594 15H18.3594ZM18.3594 11C18.9117 11 19.3594 11.4477 19.3594 12C19.3594 12.5523 18.9117 13 18.3594 13H13.3594C12.8071 13 12.3594 12.5523 12.3594 12C12.3594 11.4477 12.8071 11 13.3594 11H18.3594ZM18.3594 7C18.9117 7 19.3594 7.44772 19.3594 8C19.3594 8.55228 18.9117 9 18.3594 9H13.3594C12.8071 9 12.3594 8.55228 12.3594 8C12.3594 7.44772 12.8071 7 13.3594 7H18.3594Z"
      fill="currentColor"
    />
  </svg>
);

interface TransportBarProps {
  audioEngine?: AudioEngine;
  transportState: TransportState;
  position: TransportPosition;
  bpm: number;
  onBpmChange: (bpm: number) => void;
  timeSignature: TimeSignature;
  onTimeSignatureChange?: (sig: TimeSignature) => void;
  onPlay: () => void;
  onStop: () => void;
  onRecord: () => void;
  onRewind: () => void;
  onFastForward: () => void;
  onReturnToStart: () => void;
  loopEnabled: boolean;
  onToggleLoop: () => void;
  loopRange?: LoopRange;
  onUpdateLoopRange?: (range: LoopRange) => void;
  metronomeEnabled: boolean;
  onToggleMetronome: () => void;
  countInMeasures: number;
  onCountInChange: (measures: number) => void;
  countInBeat?: number | null;
  quantizeOptions: QuantizeOptions;
  onQuantizeChange: (options: QuantizeOptions) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onOpenExport?: () => void;
  onOpenMidiSettings?: () => void;
  midiDeviceName?: string | null;
  midiSupported?: boolean;
  lastMidiActivity?: { pitch: number; velocity: number; channel: number; type: 'on' | 'off'; timestamp: number } | null;
  activeLivePitchesCount?: number;
  viewMode?: DawViewMode;
  onViewModeChange?: (mode: DawViewMode) => void;
  layoutConfig?: SplitLayoutConfig;
  onLayoutConfigChange?: (config: SplitLayoutConfig) => void;
  showVirtualKeyboard?: boolean;
  onToggleVirtualKeyboard?: () => void;
  showVelocityLane?: boolean;
  onToggleVelocityLane?: () => void;
  autoScroll?: boolean;
  onToggleAutoScroll?: () => void;
  showLoopControls?: boolean;
  showPositionDisplay?: boolean;
}

export const TransportBar: React.FC<TransportBarProps> = ({
  audioEngine,
  transportState,
  position,
  bpm,
  onBpmChange,
  timeSignature,
  onTimeSignatureChange,
  onPlay,
  onStop,
  onRecord,
  onRewind,
  onFastForward,
  onReturnToStart,
  loopEnabled,
  onToggleLoop,
  loopRange,
  onUpdateLoopRange,
  metronomeEnabled,
  onToggleMetronome,
  countInMeasures,
  onCountInChange,
  countInBeat,
  quantizeOptions,
  onQuantizeChange,
  autoScroll = true,
  onToggleAutoScroll,
  showLoopControls = false,
  showPositionDisplay = false,
}) => {
  const isPlaying = transportState === 'playing';
  const isRecording = transportState === 'recording';
  const isCountIn = transportState === 'count-in';

  const quantizeGrids: QuantizeGrid[] = ['Off', '1/4', '1/8', '1/16', '1/32', '1/8T', '1/16T'];

  // Handle loop measure changes (inclusive measures) with live push/pull and active enabling
  const handleLoopStartChange = (val: number) => {
    if (!loopRange || !onUpdateLoopRange) return;
    if (isNaN(val) || val < 1) return;
    const newStart = Math.max(1, Math.round(val));
    // If start is pushed beyond end, push end along with it
    const newEnd = Math.max(newStart, loopRange.endMeasure);
    onUpdateLoopRange({ ...loopRange, enabled: true, startMeasure: newStart, endMeasure: newEnd });
  };

  const handleLoopEndChange = (val: number) => {
    if (!loopRange || !onUpdateLoopRange) return;
    if (isNaN(val) || val < 1) return;
    const newEnd = Math.max(1, Math.round(val));
    // If end is pulled below start, pull start along with it
    const newStart = Math.min(newEnd, loopRange.startMeasure);
    onUpdateLoopRange({ ...loopRange, enabled: true, startMeasure: newStart, endMeasure: newEnd });
  };

  return (
    <header
      id="transport-bar"
      className="bg-[#141210] border-b border-[#2D2820] text-[#EBE6DB] select-none shadow-xs"
    >
      {/* LIGNE 1 (Zone 1) : Deck de Pilotage Master (Transport & Horloge de Production) */}
      <div
        id="transport-main-deck"
        className="px-3 py-1.5 flex items-center gap-2 flex-wrap bg-gradient-to-r from-[#171513] via-[#1a1714] to-[#151311] border-b border-[#2e261d] shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]"
      >
        {/* BLOC 1: Commandes de transport & boucle */}
        <div className="flex items-center gap-1 bg-[#1e1b17] p-1 border border-[#443828] shadow-xs shrink-0 rounded-xs">
          {/* Return to Start |< */}
          <button
            id="transport-start-btn"
            onClick={onReturnToStart}
            className="flex items-center justify-center w-7 h-7 text-[#b5aa99] hover:text-[#f8d070] hover:bg-[#28231c] transition-colors rounded-xs"
            title="Return to start (0:00:00 / Bar 1) [Key: 0 / Home]"
          >
            <SkipBack className="w-3.5 h-3.5" />
          </button>

          {/* Rewind << */}
          <button
            id="transport-rewind-btn"
            onClick={onRewind}
            className="flex items-center justify-center w-7 h-7 text-[#b5aa99] hover:text-[#f8d070] hover:bg-[#28231c] transition-colors rounded-xs"
            title="Rewind 1 bar (<<) [Key: Left Arrow / ,]"
          >
            <Rewind className="w-3.5 h-3.5 fill-current" />
          </button>

          {/* Fast Forward >> */}
          <button
            id="transport-forward-btn"
            onClick={onFastForward}
            className="flex items-center justify-center w-7 h-7 text-[#b5aa99] hover:text-[#f8d070] hover:bg-[#28231c] transition-colors rounded-xs"
            title="Forward 1 bar (>>) [Key: Right Arrow / .]"
          >
            <FastForward className="w-3.5 h-3.5 fill-current" />
          </button>

          <div className="h-4 w-px bg-[#443828] mx-0.5" />

          {/* Stop Button */}
          <button
            id="transport-stop-btn"
            onClick={onStop}
            className="flex items-center justify-center w-7 h-7 text-[#b5aa99] hover:text-[#f8d070] hover:bg-[#28231c] transition-colors rounded-xs"
            title="Stop transport"
          >
            <Square className="w-3.5 h-3.5 fill-current" />
          </button>

          {/* Play Button - Iconic High-End Amber Gold */}
          <button
            id="transport-play-btn"
            onClick={isPlaying ? onStop : onPlay}
            className={`flex items-center gap-1.5 px-3 h-7 font-bold text-xs transition-all border rounded-xs ${
              isPlaying
                ? 'bg-[#2a241b] text-[#fbbf24] border-[#fbbf24] shadow-[0_0_12px_rgba(251,191,36,0.35)]'
                : 'bg-amber-500 hover:bg-amber-400 text-stone-950 border-amber-600 shadow-[0_1px_3px_rgba(0,0,0,0.5)]'
            }`}
            title={isPlaying ? 'Pause/Stop (Space)' : 'Play (Space)'}
          >
            <Play className={`w-3.5 h-3.5 ${isPlaying ? 'fill-[#fbbf24]' : 'fill-stone-950'}`} />
            <span>Play</span>
          </button>

          {/* Record Button - Crimson Studio Glow */}
          <button
            id="transport-record-btn"
            onClick={isRecording || isCountIn ? onStop : onRecord}
            className={`flex items-center gap-1.5 px-3 h-7 font-bold text-xs transition-all border rounded-xs ${
              isRecording
                ? 'bg-rose-600 text-white border-rose-500 animate-pulse shadow-[0_0_12px_rgba(244,63,94,0.5)]'
                : isCountIn
                ? 'bg-amber-600 text-white border-amber-500 animate-bounce'
                : 'hover:bg-[#2a241b] text-rose-500 hover:text-rose-400 border-transparent'
            }`}
            title={isRecording ? 'Stop recording (Space)' : 'Arm & Record (R)'}
          >
            <Circle
              className={`w-3.5 h-3.5 ${isRecording || isCountIn ? 'fill-white' : 'fill-rose-500'}`}
            />
            <span>{isCountIn ? `Count ${countInBeat ?? ''}` : 'Rec'}</span>
          </button>

          {/* Loop Zone: Toggle & Editable Range Controls - Optionnel via Menu Affichage */}
          {showLoopControls && (
            <>
              <div className="h-4 w-px bg-[#3D362C] mx-0.5" />
              <div className="flex items-center gap-1 pl-0.5">
                <button
                  id="transport-loop-btn"
                  onClick={onToggleLoop}
                  className={`flex items-center gap-1 px-1.5 h-7 text-xs font-semibold transition-colors shrink-0 border ${
                    loopEnabled
                      ? 'bg-[#F5C767]/20 text-[#F5C767] border-[#F5C767] shadow-[0_0_6px_rgba(245,199,103,0.25)]'
                      : 'text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] border-transparent'
                  }`}
                  title={`Loop Mode (${loopEnabled ? 'Enabled' : 'Disabled'})`}
                >
                  <Repeat className="w-3.5 h-3.5 shrink-0" />
                  <span
                    className={`w-[22px] h-3.5 inline-flex items-center justify-center text-center font-mono text-[8px] font-bold uppercase ${
                      loopEnabled ? 'bg-[#F5C767] text-[#1B1B1B]' : 'bg-[#25211B] text-[#A89F91]'
                    }`}
                  >
                    {loopEnabled ? 'ON' : 'OFF'}
                  </span>
                </button>

                {/* Editable Loop Range Inputs (Bar Start -> Bar End) */}
                {loopRange && (
                  <div
                    className={`flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-mono border transition-opacity ${
                      loopEnabled
                        ? 'border-[#3D362C] bg-[#161412] text-[#EBE6DB]'
                        : 'border-[#2D2820] bg-[#141210] text-[#A89F91]/50 opacity-70'
                    }`}
                    title="Loop Region Measures (Editable: Drag markers in ruler or adjust here)"
                  >
                    {/* L Control */}
                    <div
                      className="flex items-center gap-0.5"
                      onWheel={(e) => {
                        e.preventDefault();
                        handleLoopStartChange(loopRange.startMeasure + (e.deltaY < 0 ? 1 : -1));
                      }}
                    >
                      <span className="text-[9px] text-[#A89F91] font-semibold mr-0.5">L:</span>
                      <button
                        type="button"
                        onClick={() => handleLoopStartChange(loopRange.startMeasure - 1)}
                        className="w-4 h-4 flex items-center justify-center bg-[#25211B] hover:bg-[#F5C767] hover:text-[#1B1B1B] text-[#A89F91] text-[10px] active:scale-95 leading-none transition-colors border border-[#3D362C]"
                        title="Decrease Loop Start (Bar) - fits live"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={999}
                        step={1}
                        value={loopRange.startMeasure}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (!isNaN(v)) handleLoopStartChange(v);
                        }}
                        className="w-7 bg-transparent text-center font-bold text-[#F5C767] focus:outline-none focus:bg-[#25211B] py-0.5 text-xs"
                        title="Loop Start Measure (Bar) - Fits live during playback (Scroll or click +/-)"
                      />
                      <button
                        type="button"
                        onClick={() => handleLoopStartChange(loopRange.startMeasure + 1)}
                        className="w-4 h-4 flex items-center justify-center bg-[#25211B] hover:bg-[#F5C767] hover:text-[#1B1B1B] text-[#A89F91] text-[10px] active:scale-95 leading-none transition-colors border border-[#3D362C]"
                        title="Increase Loop Start (Bar) - fits live"
                      >
                        +
                      </button>
                    </div>

                    <span className="text-[#A89F91]/50 px-0.5">&rarr;</span>

                    {/* R Control */}
                    <div
                      className="flex items-center gap-0.5"
                      onWheel={(e) => {
                        e.preventDefault();
                        handleLoopEndChange(loopRange.endMeasure + (e.deltaY < 0 ? 1 : -1));
                      }}
                    >
                      <span className="text-[9px] text-[#A89F91] font-semibold mr-0.5">R:</span>
                      <button
                        type="button"
                        onClick={() => handleLoopEndChange(loopRange.endMeasure - 1)}
                        className="w-4 h-4 flex items-center justify-center bg-[#25211B] hover:bg-[#F5C767] hover:text-[#1B1B1B] text-[#A89F91] text-[10px] active:scale-95 leading-none transition-colors border border-[#3D362C]"
                        title="Decrease Loop End (Bar) - fits live"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={999}
                        step={1}
                        value={loopRange.endMeasure}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (!isNaN(v)) handleLoopEndChange(v);
                        }}
                        className="w-7 bg-transparent text-center font-bold text-[#F5C767] focus:outline-none focus:bg-[#25211B] py-0.5 text-xs"
                        title="Loop End Measure (Bar) - Fits live during playback (Scroll or click +/-)"
                      />
                      <button
                        type="button"
                        onClick={() => handleLoopEndChange(loopRange.endMeasure + 1)}
                        className="w-4 h-4 flex items-center justify-center bg-[#25211B] hover:bg-[#F5C767] hover:text-[#1B1B1B] text-[#A89F91] text-[10px] active:scale-95 leading-none transition-colors border border-[#3D362C]"
                        title="Increase Loop End (Bar) - fits live"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* BLOC 2: Position temporelle, Tempo & Métronome */}
        <div className="flex items-center gap-2 shrink-0">
          {/* LCD Position Display - Optionnel via Menu Affichage */}
          {showPositionDisplay && (
            <div className="flex items-center gap-2.5 bg-[#1C1A17] px-2.5 py-1 border border-[#3D362C] font-mono text-xs shadow-xs">
              <div className="flex items-center gap-1.5 text-[#EBE6DB]">
                <Clock className="w-3.5 h-3.5 text-[#F5C767]" />
                <span className="text-[#A89F91] text-[10px] uppercase tracking-wider font-sans">Pos</span>
                <span className="text-[#F5C767] font-bold tracking-widest text-xs min-w-[70px]">
                  {formatPosition(position)}
                </span>
              </div>
              <div className="h-3.5 w-px bg-[#3D362C]" />
              <span className="text-[#A89F91] text-[11px] min-w-[50px] text-right">
                {formatTime(position.totalSeconds)}
              </span>
            </div>
          )}

          {/* BPM Stepper Hub */}
          <div className="flex items-center bg-[#1C1A17] border border-[#3D362C] p-0.5 text-xs shadow-xs">
            <button
              onClick={() => onBpmChange(Math.max(30, bpm - 1))}
              className="w-5 h-6 flex items-center justify-center text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] transition-colors"
              title="Decrease BPM (-1)"
            >
              <Minus className="w-3 h-3" />
            </button>
            <div className="flex items-center px-1">
              <span className="text-[#A89F91] text-[9px] uppercase mr-1 font-bold">BPM</span>
              <input
                id="bpm-input"
                type="number"
                min={30}
                max={300}
                value={bpm}
                onChange={(e) => onBpmChange(Math.max(30, Math.min(320, Number(e.target.value) || 120)))}
                className="w-10 bg-transparent text-[#F5C767] font-mono font-bold focus:outline-none text-center text-xs"
              />
            </div>
            <button
              onClick={() => onBpmChange(Math.min(300, bpm + 1))}
              className="w-5 h-6 flex items-center justify-center text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] transition-colors"
              title="Increase BPM (+1)"
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>

          {/* Time Signature */}
          <div className="flex items-center bg-[#1C1A17] border border-[#3D362C] px-2 h-7 text-xs text-[#EBE6DB] shadow-xs">
            <span className="text-[#A89F91] text-[9px] uppercase mr-1 font-bold">Sig</span>
            {onTimeSignatureChange ? (
              <select
                value={`${timeSignature.beats}/${timeSignature.beatType}`}
                onChange={(e) => {
                  const [b, bt] = e.target.value.split('/').map(Number);
                  onTimeSignatureChange({ beats: b, beatType: bt });
                }}
                className="bg-transparent text-[#F5C767] focus:outline-none cursor-pointer font-mono font-bold text-xs"
              >
                <option value="4/4" className="bg-[#1C1A17] text-[#F5C767]">4/4</option>
                <option value="3/4" className="bg-[#1C1A17] text-[#F5C767]">3/4</option>
                <option value="6/8" className="bg-[#1C1A17] text-[#F5C767]">6/8</option>
                <option value="2/4" className="bg-[#1C1A17] text-[#F5C767]">2/4</option>
                <option value="5/4" className="bg-[#1C1A17] text-[#F5C767]">5/4</option>
              </select>
            ) : (
              <span className="font-mono font-bold text-[#F5C767]">
                {timeSignature.beats}/{timeSignature.beatType}
              </span>
            )}
          </div>

          {/* Metronome ON/OFF & Count-In Bay */}
          <div className="flex items-center gap-1 bg-[#1C1A17] p-0.5 border border-[#3D362C] shadow-xs">
            <button
              id="metronome-toggle-btn"
              onClick={onToggleMetronome}
              className={`flex items-center gap-1 px-1.5 h-6 text-xs font-semibold transition-colors shrink-0 border ${
                metronomeEnabled
                  ? 'bg-[#F5C767]/20 text-[#F5C767] border-[#F5C767] shadow-xs'
                  : 'text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] border-transparent'
              }`}
              title={`Metronome: ${metronomeEnabled ? 'ENABLED (Click to disable)' : 'DISABLED (Click to enable)'} [Shortcut: M]`}
            >
              <MetronomeIcon className={`w-[11px] h-3.5 shrink-0 ${metronomeEnabled ? 'text-[#F5C767]' : 'text-[#A89F91]'}`} />
              <span
                className={`w-[22px] h-3.5 inline-flex items-center justify-center text-center font-mono text-[8px] font-bold uppercase ${
                  metronomeEnabled ? 'bg-[#F5C767] text-[#1B1B1B]' : 'bg-[#25211B] text-[#A89F91]'
                }`}
              >
                {metronomeEnabled ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Count-in selector */}
            <div className="flex items-center text-[10px] text-[#EBE6DB] px-1 border-l border-[#3D362C]">
              <span className="text-[#A89F91] mr-1 text-[9px] uppercase font-bold">Pre:</span>
              <select
                value={countInMeasures}
                onChange={(e) => onCountInChange(Number(e.target.value))}
                className="bg-transparent text-[#F5C767] focus:outline-none cursor-pointer text-[10px] font-mono"
                title="Count-in measures before recording starts"
              >
                <option value={0} className="bg-[#1C1A17] text-[#F5C767]">0 bar</option>
                <option value={1} className="bg-[#1C1A17] text-[#F5C767]">1 bar</option>
                <option value={2} className="bg-[#1C1A17] text-[#F5C767]">2 bars</option>
              </select>
            </div>

            {/* Auto-Scroll Follow Playhead toggle */}
            {onToggleAutoScroll && (
              <button
                id="transport-autoscroll-toggle-btn"
                type="button"
                onClick={onToggleAutoScroll}
                className={`flex items-center gap-1 px-1.5 h-6 text-xs font-semibold transition-colors shrink-0 ml-0.5 border ${
                  autoScroll
                    ? 'bg-[#F5C767]/20 text-[#F5C767] border-[#F5C767] shadow-xs'
                    : 'text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] border-transparent'
                }`}
                title={`Auto-scroll follow playhead [Shortcut: F]: ${autoScroll ? 'ENABLED' : 'DISABLED'}`}
              >
                <ArrowRightToLine className={`w-3.5 h-3.5 shrink-0 ${autoScroll ? 'text-[#F5C767]' : 'text-[#A89F91]'}`} />
                <span
                  className={`w-[22px] h-3.5 inline-flex items-center justify-center text-center font-mono text-[8px] font-bold uppercase ${
                    autoScroll ? 'bg-[#F5C767] text-[#1B1B1B]' : 'bg-[#25211B] text-[#A89F91]'
                  }`}
                >
                  {autoScroll ? 'ON' : 'OFF'}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* BLOC 3: Contrôles de quantification */}
        <div className="flex items-center gap-1.5 bg-[#1C1A17] px-2 py-0.5 border border-[#3D362C] text-xs shadow-xs shrink-0">
          <Sliders className="w-3 h-3 text-[#F5C767]" />
          <span className="text-[#A89F91] text-[9px] uppercase font-bold">Quantize</span>

          {/* Grid selector */}
          <select
            value={quantizeOptions.grid}
            onChange={(e) =>
              onQuantizeChange({ ...quantizeOptions, grid: e.target.value as QuantizeGrid })
            }
            className="bg-[#141210] text-[#F5C767] px-1.5 py-0.5 border border-[#3D362C] text-[11px] focus:outline-none cursor-pointer font-mono"
          >
            {quantizeGrids.map((g) => (
              <option key={g} value={g} className="bg-[#1C1A17] text-[#F5C767]">
                {g}
              </option>
            ))}
          </select>

          {/* Strength slider */}
          {quantizeOptions.grid !== 'Off' && (
            <div className="flex items-center gap-1 ml-0.5">
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(quantizeOptions.strength * 100)}
                onChange={(e) =>
                  onQuantizeChange({ ...quantizeOptions, strength: Number(e.target.value) / 100 })
                }
                className="w-12 h-1 bg-[#3D362C] appearance-none cursor-pointer accent-[#F5C767]"
                title={`Quantize Strength: ${Math.round(quantizeOptions.strength * 100)}%`}
              />
              <span className="text-[9px] text-[#A89F91] w-6 font-mono font-semibold">
                {Math.round(quantizeOptions.strength * 100)}%
              </span>
            </div>
          )}

          {/* Start / Duration toggles */}
          <div className="flex items-center gap-1.5 text-[9px] ml-0.5 text-[#A89F91]">
            <label className="flex items-center gap-0.5 cursor-pointer hover:text-[#F5C767]">
              <input
                type="checkbox"
                checked={quantizeOptions.quantizeStart}
                onChange={(e) =>
                  onQuantizeChange({ ...quantizeOptions, quantizeStart: e.target.checked })
                }
                className="accent-[#F5C767] scale-90"
              />
              <span>Start</span>
            </label>
            <label className="flex items-center gap-0.5 cursor-pointer hover:text-[#F5C767]">
              <input
                type="checkbox"
                checked={quantizeOptions.quantizeDuration}
                onChange={(e) =>
                  onQuantizeChange({ ...quantizeOptions, quantizeDuration: e.target.checked })
                }
                className="accent-[#F5C767] scale-90"
              />
              <span>Dur</span>
            </label>
          </div>
        </div>
      </div>

      {/* LIGNE 2 (Zone 2) : Sound Engine & DSP Effects Rack (Moteur Audio, Instruments & Espace Acoustique) */}
      <div
        id="transport-sound-rack"
        className="bg-gradient-to-r from-[#0d151c] via-[#0f1720] to-[#0c1318] px-3 py-1 flex items-center justify-between gap-2 text-xs border-b border-cyan-950/40 shadow-[inset_0_1px_0_rgba(6,182,212,0.06)]"
      >
        <div className="flex items-center gap-2 flex-wrap">
          {/* Virtual Instrument Selector */}
          {audioEngine && <InstrumentSelector audioEngine={audioEngine} />}

          {/* Piano Effects & Acoustic Space Rack */}
          {audioEngine && <PianoEffectsPopover audioEngine={audioEngine} />}
        </div>
      </div>
    </header>
  );
};
